import React from 'react'
import { GridRenderCellParams } from '@mui/x-data-grid'
import { Tooltip } from 'components'
import strings from 'strings'
import styles from '../vehicleAssignmentsTable/assignedDriversCell.module.scss'
import { AssignedDriver } from 'interfaces/IDrivers'
import { numberComparator, numberOrStringComparator } from 'tools/comparators'
import { formatRiskCell } from 'tools/helper'

const t = strings.TABLE_DEFAULTS

const DriversCell: React.FC<{
  drivers: AssignedDriver[]
  truncateDriverDisplay: (
    name: string,
    id: string,
    maxLength?: number
  ) => { name: string; id: string }
}> = ({ drivers, truncateDriverDisplay }) => {
  if (!drivers || drivers.length === 0) {
    return (
      <div className={styles.emptyState}>
        <span>{t.DASH_VALUE}</span>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {drivers.map((driver, index) => {
        const { name: displayName, id: displayId } = truncateDriverDisplay(
          driver.driverName,
          driver.eldDriverId
        )
        const fullDisplayText = `${driver.driverName} (${driver.eldDriverId})`

        return (
          <div
            key={`${driver.eldDriverId}-${index}`}
            className={styles.driverItem}
          >
            <Tooltip title={fullDisplayText} containerClassName="ellipsis">
              <span>
                <span className={styles.driverName}>{displayName}</span>
                <span className={styles.driverId}>{displayId}</span>
              </span>
            </Tooltip>
          </div>
        )
      })}
    </div>
  )
}

const getRecentTeamDrivingTableColumns = (
  truncateDriverDisplay: (
    name: string,
    id: string,
    maxLength?: number
  ) => { name: string; id: string },
  useKilometers: boolean = false
) => {
  const distanceLabel = useKilometers ? 'Kilometers' : 'Miles'
  return [
    {
      field: 'rowId',
      headerName: 'ID',
      width: 60,
      minWidth: 60,
      flex: 0.3,
    },
    {
      field: 'numDrivers',
      headerName: 'Num Drivers',
      width: 100,
      minWidth: 100,
      flex: 0.3,
    },
    {
      field: 'vin',
      headerName: 'VIN',
      width: 180,
      minWidth: 180,
      flex: 1,
      sortable: true,
      sortComparator: numberOrStringComparator,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Tooltip title={params.row.vin} containerClassName="ellipsis">
            <span>{params.row.vin}</span>
          </Tooltip>
        )
      },
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      minWidth: 120,
      flex: 0.6,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Tooltip title={params.row.date} containerClassName="ellipsis">
            <span>{params.row.date}</span>
          </Tooltip>
        )
      },
    },
    {
      field: 'assetDisplayName',
      headerName: 'Vehicle',
      width: 180,
      minWidth: 180,
      flex: 1,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Tooltip
            title={params.row.assetDisplayName}
            containerClassName="ellipsis"
          >
            <span>{params.row.assetDisplayName}</span>
          </Tooltip>
        )
      },
    },
    {
      field: 'plateNumber',
      headerName: 'Plate Number',
      width: 140,
      minWidth: 140,
      flex: 0.8,
      sortable: true,
      sortComparator: numberOrStringComparator,
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Tooltip title={params.row.plateNumber} containerClassName="ellipsis">
            <span>{params.row.plateNumber}</span>
          </Tooltip>
        )
      },
    },
    {
      field: 'milesThatDay',
      headerName: `${distanceLabel} (That Day)`,
      width: 140,
      minWidth: 140,
      flex: 0.7,
      sortable: true,
      sortComparator: numberComparator,
      valueGetter: (value: any, row: any) => {
        return row.milesThatDay !== undefined && row.milesThatDay !== null
          ? formatRiskCell(row.milesThatDay)
          : t.DASH_VALUE
      },
    },
    {
      field: 'drivers',
      headerName: 'Drivers',
      width: 200,
      minWidth: 200,
      flex: 1.2,
      sortable: false,
      cellClassName: 'drivers-cell',
      renderCell: function (params: GridRenderCellParams) {
        return (
          <DriversCell
            drivers={params.row.drivers || []}
            truncateDriverDisplay={truncateDriverDisplay}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        const drivers = row.drivers || []
        if (!Array.isArray(drivers) || drivers.length === 0) {
          return t.DASH_VALUE
        }
        return drivers
          .map(
            (driver: AssignedDriver) =>
              `${driver.driverName}(${driver.eldDriverId})`
          )
          .join(', ')
      },
    },
  ]
}

export { getRecentTeamDrivingTableColumns }
